import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { FindAllWithdrawalsAdminQueryQuery, QueryFindAllWithdrawalsAdminArgs, Withdrawal } from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllWithdrawalsAdminQuery = gql`
  query FindAllWithdrawalsAdminQuery(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $searchText: String
    $duration: String
  ) {
    findAllWithdrawalsAdmin(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      searchText: $searchText
      duration: $duration
    ) {
      withdrawals {
        id
        transaction {
            id 
            amount 
            status
            wallet {
                id
                flAccountReference
                barterId
                balance
            }
            user {
                id
                admin{
                    id
                    merchantProfile {
                        companyName
                        businessName
                    }
                }
            }
            status
            currency
        }
        bankPreference{
          id
          bank {
            id 
            bank 
            bankCode
          }
          accountNo
        }
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllWithdrawalsAdmin {
  withdrawals: Withdrawal[]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreWithdrawals: boolean;
}

interface Props {
  variables: QueryFindAllWithdrawalsAdminArgs;
  children: (data: WithFindAllWithdrawalsAdmin) => React.ReactElement | null;
}

export const FindAllWithdrawalsAdminController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllWithdrawalsAdminQueryQuery,
    QueryFindAllWithdrawalsAdminArgs
  >(findAllWithdrawalsAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  // Subscription for new transactions (optional, adjust as needed)
//   useSubscription(newFindAllOffersAdminSubscription);

  const findAllWithdrawalsAdminData = data?.findAllWithdrawalsAdmin?.withdrawals || [];
  const page = data?.findAllWithdrawalsAdmin?.page || variables.page;
  const pageSize = data?.findAllWithdrawalsAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllWithdrawalsAdmin?.total || 0;
  const totalPages = data?.findAllWithdrawalsAdmin?.totalPages || 0;
  const hasMoreWithdrawals = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreWithdrawals) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllWithdrawalsAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllWithdrawalsAdmin: {
            ...fetchMoreResult.findAllWithdrawalsAdmin,
            data: [
              ...(prev?.findAllWithdrawalsAdmin?.withdrawals || []),
              ...(fetchMoreResult.findAllWithdrawalsAdmin.withdrawals || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreWithdrawals]);

  return children({
    withdrawals: findAllWithdrawalsAdminData as any,
    loading,
    loadMore,
    hasMoreWithdrawals,
    page,
    pageSize,
    total,
    totalPages,
  });
};